import { useMemo, useState } from "react";
import { ArrowRight, CheckCircle2, CircleDashed, ShieldCheck, X } from "lucide-react";
import { WorkforcePlan } from "./lng-intelligence-api";
import "./signal-intelligence.css";

const PLAN_KEY = "lng:workforce-plan";
const label = (value: string) =>
  value.split("_").map((part) => part[0].toUpperCase() + part.slice(1)).join(" ");

type View = {
  family: string;
  title: string;
  question: string;
  groups: Array<[string, string[]]>;
  next: string;
};

const VIEWS: Record<"planning" | "commodity_hedge", View> = {
  planning: {
    family: "Commercial Planning",
    title: "Planning",
    question: "What must happen, in what order, and who owns each milestone?",
    groups: [
      ["Execution plan", ["Actions and owners", "Target dates", "Decision dependencies"]],
      ["Critical path", ["Physical milestones", "Commercial deadlines", "Blocking exceptions"]],
    ],
    next: "Confirm the executable sequence and accountable owners.",
  },
  commodity_hedge: {
    family: "Market Risk",
    title: "Commodity Hedge",
    question: "How should the incremental commodity exposure be controlled?",
    groups: [
      ["Exposure", ["Open commodity exposure", "Tenor and location", "Residual basis risk"]],
      ["Hedge proposal", ["Eligible instruments", "Coverage ratio", "Expected hedge cost"]],
    ],
    next: "Approve, revise or reject the hedge proposal.",
  },
};

function restorePlan(): WorkforcePlan | null {
  try {
    const value = sessionStorage.getItem(PLAN_KEY);
    return value ? JSON.parse(value) as WorkforcePlan : null;
  } catch {
    return null;
  }
}

function BlockWorkspace({ block }: { block: keyof typeof VIEWS }) {
  const plan = useMemo(restorePlan, []);
  const view = VIEWS[block];
  const requirements = plan?.requirements.filter((item) => item.block === block) ?? [];
  const required = requirements.length > 0;
  const reasons = Array.from(new Set(requirements.flatMap((item) => item.reason_codes)));
  const [detail, setDetail] = useState<{ group: string; field: string } | null>(null);

  return <section className="lng-work lng-agent lng-block">
    <header className="lng-work__header">
      <div><p>{view.family.toUpperCase()}</p><h1>{view.title}</h1></div>
      <span className={required ? "lng-block__state required" : "lng-block__state"}>
        {required ? <CheckCircle2 size={14}/> : <CircleDashed size={14}/>}
        {required ? "Required for this opportunity" : "Not required for this opportunity"}
      </span>
    </header>
    <section className="lng-block__question">
      <small>DECISION QUESTION</small><b>{view.question}</b>
    </section>
    <section className="lng-agent__summary">
      <div><small>OPPORTUNITY</small><strong>{plan?.opportunity_id ?? "—"}</strong></div>
      <div><small>BUSINESS STATUS</small><strong>{required ? "—" : "Not applicable"}</strong></div>
      <div><small>ROUTING BASIS</small><strong>{reasons.length ? reasons.map(label).join(", ") : "—"}</strong></div>
      <div><small>OWNER</small><strong>—</strong></div>
    </section>
    <section className="lng-block__grid">
      {view.groups.map(([title, fields]) => <article className="lng-agent__panel" key={title}>
        <header><b>{title}</b><span>Authoritative source</span></header>
        <div className="lng-block__fields">
          {fields.map((field) => <button type="button" key={field} onClick={() => setDetail({ group: title, field })}>
            <span>{field}</span><b>{required ? "Open record" : "No source record"}</b><ArrowRight size={13}/>
          </button>)}
        </div>
      </article>)}
    </section>
    <section className="lng-agent__handoff">
      <div><small>NEXT BUSINESS ACTION</small><b>{view.next}</b></div><ShieldCheck size={18}/>
    </section>
    {detail && <div className="lng-block__modal" role="dialog" aria-modal="true" aria-label={`${detail.field} details`}>
      <article>
        <button type="button" aria-label="Close details" onClick={() => setDetail(null)}><X size={18}/></button>
        <small>{detail.group.toUpperCase()}</small><h2>{detail.field}</h2>
        <dl>
          <div><dt>Opportunity</dt><dd>{plan?.opportunity_id ?? "No opportunity selected"}</dd></div>
          <div><dt>Status</dt><dd>{required ? "—" : "Not required"}</dd></div>
          <div><dt>Routing basis</dt><dd>{reasons.length ? reasons.map(label).join(", ") : "No trigger selected"}</dd></div>
        </dl>
      </article>
    </div>}
  </section>;
}

export const PlanningWorkspace = () => <BlockWorkspace block="planning"/>;
export const HedgeWorkspace = () => <BlockWorkspace block="commodity_hedge"/>;
